import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DataTable, type Column } from '../../components/table/DataTable';
import FormattedDateRangeInput from '../../components/ui/FormattedDateRangeInput';
import MultiSelectChips from '../../components/ui/MultiSelectChips';
import { getFileCategories, getSubFileCategories } from '../../services/criminalCaseRequests';
import {
  fetchLegalKnowledgeArchiveList,
  type LegalKnowledgeArchiveListRequestBody,
  type LegalKnowledgeArchiveListRow,
} from '../../services/legalKnowledgeArchive';
import { usePermission } from '../../rbac/hooks';
import { LK_PERMISSIONS as LK } from '../../rbac/legalKnowledgePermissions';
import { formatDisplayDateTime } from '../../utils/dateFormat';
import { toIsoDateEnd, toIsoDateStart } from '../../utils/reportDates';

type FilterState = {
  archiveRefNo: string;
  fileRefNo: string;
  fileCategoryIds: string[];
  subFileCategoryIds: string[];
  text: string;
  caseCompletedFrom: string;
  caseCompletedTo: string;
  publishedFrom: string;
  publishedTo: string;
};

const INITIAL_FILTER_STATE: FilterState = {
  archiveRefNo: '',
  fileRefNo: '',
  fileCategoryIds: [],
  subFileCategoryIds: [],
  text: '',
  caseCompletedFrom: '',
  caseCompletedTo: '',
  publishedFrom: '',
  publishedTo: '',
};

const SORT_KEY_MAP: Record<string, string> = {
  archiveReferenceNumber: 'REFERENCE_NUMBER',
  fileReferenceNumber: 'FILE_REFERENCE_NUMBER',
  fileCategoryName: 'FILE_CATEGORY',
  subFileCategoryName: 'SUB_FILE_CATEGORY',
  caseCompletedAt: 'CASE_COMPLETED_AT',
  casePublishedAt: 'CASE_PUBLISHED_AT',
};

const toDateRangeOrNull = (
  from: string,
  to: string,
): { from: string | null; to: string | null } => {
  const fromTrimmed = from.trim();
  const toTrimmed = to.trim();
  if (!fromTrimmed && !toTrimmed) {
    return { from: null, to: null };
  }
  const effectiveFrom = fromTrimmed || toTrimmed;
  const effectiveTo = toTrimmed || fromTrimmed;
  return {
    from: toIsoDateStart(effectiveFrom) ?? null,
    to: toIsoDateEnd(effectiveTo) ?? null,
  };
};

const cellOrDash = (value: string | null | undefined) => value?.trim() || '-';

export default function LegalKnowledgeArchivePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { has } = usePermission();
  const canViewArchivedCase = has(LK.VIEW_LEGAL_ARCHIVE) || has(LK.VIEW_ARCHIVE_CASES);
  const [pending, setPending] = useState<FilterState>(INITIAL_FILTER_STATE);
  const [applied, setApplied] = useState<FilterState>(INITIAL_FILTER_STATE);
  const [rows, setRows] = useState<LegalKnowledgeArchiveListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [sortKey, setSortKey] = useState<string>('fileReferenceNumber');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [fileCategoryOptions, setFileCategoryOptions] = useState<{ id: string; label: string }[]>([]);
  const [subCategoryOptions, setSubCategoryOptions] = useState<{ id: string; label: string }[]>([]);
  const [searchTrigger, setSearchTrigger] = useState(0);

  useEffect(() => {
    let active = true;
    const caseCompletedRange = toDateRangeOrNull(applied.caseCompletedFrom, applied.caseCompletedTo);
    const publishedRange = toDateRangeOrNull(applied.publishedFrom, applied.publishedTo);
    const body: LegalKnowledgeArchiveListRequestBody = {
      reference_number: applied.archiveRefNo.trim() || null,
      file_reference_number: applied.fileRefNo.trim() || null,
      file_category_ids: applied.fileCategoryIds.length > 0 ? applied.fileCategoryIds : null,
      sub_file_category_ids: applied.subFileCategoryIds.length > 0 ? applied.subFileCategoryIds : null,
      search_text: applied.text.trim() || null,
      case_completed_at_from: caseCompletedRange.from,
      case_completed_at_to: caseCompletedRange.to,
      published_at_from: publishedRange.from,
      published_at_to: publishedRange.to,
      sort_by: SORT_KEY_MAP[sortKey] ?? 'FILE_REFERENCE_NUMBER',
      sort_direction: sortDir.toUpperCase() as 'ASC' | 'DESC',
    };
    const apiPage = page > 0 ? page - 1 : 0;
    fetchLegalKnowledgeArchiveList(body, apiPage, pageSize)
      .then((result) => {
        if (!active) return;
        setRows(result.content);
        setTotalCount(result.total_elements);
        setTotalPages(result.total_pages);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (!active) return;
        console.error('Failed to load legal knowledge archive', err);
        setRows([]);
        setTotalCount(0);
        setTotalPages(0);
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [page, pageSize, sortKey, sortDir, applied, searchTrigger]);

  useEffect(() => {
    getFileCategories().then((result) => {
      if (!result.success) return;
      setFileCategoryOptions(result.data.map((c) => ({ id: c.id, label: c.name })));
    });
  }, []);

  useEffect(() => {
    let active = true;
    if (pending.fileCategoryIds.length === 0) {
      Promise.resolve().then(() => {
        if (active) setSubCategoryOptions([]);
      });
      return () => {
        active = false;
      };
    }
    Promise.all(pending.fileCategoryIds.map((id) => getSubFileCategories(id))).then((results) => {
      if (!active) return;
      const seen = new Set<string>();
      const merged: { id: string; label: string }[] = [];
      for (const result of results) {
        if (!result.success) continue;
        for (const item of result.data) {
          if (!seen.has(item.id)) {
            seen.add(item.id);
            merged.push({ id: item.id, label: item.name });
          }
        }
      }
      setSubCategoryOptions(merged);
    });
    return () => {
      active = false;
    };
  }, [pending.fileCategoryIds]);

  const columns = useMemo<Column<LegalKnowledgeArchiveListRow>[]>(
    () => [
      {
        key: 'archiveReferenceNumber',
        header: t('legalKnowledge.outcomes.colArchiveRefNo'),
        sortable: true,
        searchable: false,
        getValue: (r) => cellOrDash(r.archive_reference_number),
        render: (r) => <span className="rn">{cellOrDash(r.archive_reference_number)}</span>,
      },
      {
        key: 'fileReferenceNumber',
        header: t('legalKnowledge.outcomes.colFileRefNo'),
        sortable: true,
        searchable: false,
        getValue: (r) => cellOrDash(r.file_reference_number),
        render: (r) => (
          <span style={{ color: r.file_reference_number ? undefined : 'var(--t3)' }}>
            {cellOrDash(r.file_reference_number)}
          </span>
        ),
      },
      {
        key: 'fileCategoryName',
        header: t('legalKnowledge.outcomes.colFileCategory'),
        sortable: true,
        searchable: false,
        getValue: (r) => cellOrDash(r.file_category_name),
        render: (r) => (
          <span style={{ color: r.file_category_name ? undefined : 'var(--t3)' }}>
            {cellOrDash(r.file_category_name)}
          </span>
        ),
      },
      {
        key: 'subFileCategoryName',
        header: t('legalKnowledge.outcomes.colSubFileCategory'),
        sortable: true,
        searchable: false,
        getValue: (r) => cellOrDash(r.sub_file_category_name),
        render: (r) => (
          <span style={{ color: r.sub_file_category_name ? undefined : 'var(--t3)' }}>
            {cellOrDash(r.sub_file_category_name)}
          </span>
        ),
      },
      {
        key: 'caseCompletedAt',
        header: t('legalKnowledge.outcomes.colCaseCompletedAt'),
        sortable: true,
        searchable: false,
        getValue: (r) => r.case_completed_at ?? '',
        render: (r) => (
          <span style={{ fontSize: 12 }}>
            {r.case_completed_at ? formatDisplayDateTime(r.case_completed_at) : '-'}
          </span>
        ),
      },
      {
        key: 'casePublishedAt',
        header: t('legalKnowledge.archive.colPublishedAt'),
        sortable: true,
        searchable: false,
        getValue: (r) => r.case_published_at ?? '',
        render: (r) => (
          <span style={{ fontSize: 12 }}>
            {r.case_published_at ? formatDisplayDateTime(r.case_published_at) : '-'}
          </span>
        ),
      },
      {
        key: 'actions',
        header: t('common.actions'),
        sortable: false,
        searchable: false,
        getValue: () => '',
        render: (r) => (
          <div className="act-wrap">
            {canViewArchivedCase ? (
              <button
                type="button"
                className="act-b"
                title={t('legalKnowledge.archive.viewArchivedCaseDetailsTip')}
                onClick={() => navigate(`/legal-knowledge/archive/${r.extracted_case_outcome_id}`)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              </button>
            ) : null}
          </div>
        ),
      },
    ],
    [canViewArchivedCase, navigate, t],
  );

  const handleReset = () => {
    setPending(INITIAL_FILTER_STATE);
    setLoading(true);
    setApplied(INITIAL_FILTER_STATE);
    setPage(0);
    setSearchTrigger((n) => n + 1);
  };

  return (
    <>
      <div className="bc">
        <span>{t('legalKnowledge.archive.breadcrumb')}</span>
      </div>

      <div className="page-hdr">
        <div>
          <div className="pg-title">{t('legalKnowledge.archive.title')}</div>
        </div>
      </div>

      <div
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--rs)',
          padding: '20px 24px',
          marginBottom: 20,
        }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
          <div className="ccr-fg">
            <label>{t('legalKnowledge.outcomes.archiveReferenceNumber')}</label>
            <input
              className="ccr-inp"
              type="text"
              placeholder={t('legalKnowledge.outcomes.archiveReferenceNumberPlaceholder')}
              value={pending.archiveRefNo}
              onChange={(e) => setPending((p) => ({ ...p, archiveRefNo: e.target.value }))}
            />
          </div>
          <div className="ccr-fg">
            <label>{t('legalKnowledge.outcomes.fileReferenceNumber')}</label>
            <input
              className="ccr-inp"
              type="text"
              placeholder={t('legalKnowledge.outcomes.fileReferenceNumberPlaceholder')}
              value={pending.fileRefNo}
              onChange={(e) => setPending((p) => ({ ...p, fileRefNo: e.target.value }))}
            />
          </div>
          <MultiSelectChips
            label={t('legalKnowledge.outcomes.fileCategories')}
            value={pending.fileCategoryIds}
            options={fileCategoryOptions}
            placeholder={t('legalKnowledge.outcomes.fileCategoriesPlaceholder')}
            onChange={(vals) => setPending((p) => ({ ...p, fileCategoryIds: vals, subFileCategoryIds: [] }))}
          />
          <MultiSelectChips
            label={t('legalKnowledge.outcomes.subFileCategories')}
            value={pending.subFileCategoryIds}
            options={subCategoryOptions}
            placeholder={t('legalKnowledge.outcomes.subFileCategoriesPlaceholder')}
            onChange={(vals) => setPending((p) => ({ ...p, subFileCategoryIds: vals }))}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginTop: 16 }}>
          <div className="ccr-fg">
            <label>{t('legalKnowledge.outcomes.enterText')}</label>
            <input
              className="ccr-inp"
              type="text"
              placeholder={t('legalKnowledge.outcomes.enterTextPlaceholder')}
              value={pending.text}
              onChange={(e) => setPending((p) => ({ ...p, text: e.target.value }))}
            />
          </div>
          <div className="ccr-fg">
            <label>{t('legalKnowledge.outcomes.caseCompletedFrom')}</label>
            <FormattedDateRangeInput
              from={pending.caseCompletedFrom}
              to={pending.caseCompletedTo}
              onChange={(f, to) => setPending((p) => ({ ...p, caseCompletedFrom: f, caseCompletedTo: to }))}
            />
          </div>
          <div className="ccr-fg">
            <label>{t('legalKnowledge.archive.publishedFrom')}</label>
            <FormattedDateRangeInput
              from={pending.publishedFrom}
              to={pending.publishedTo}
              onChange={(f, to) => setPending((p) => ({ ...p, publishedFrom: f, publishedTo: to }))}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
          <button type="button" className="mfb" onClick={handleReset}>
            {t('common.reset')}
          </button>
          <button
            type="button"
            className="mfb mfb-dk"
            onClick={() => {
              setLoading(true);
              setApplied(pending);
              setPage(0);
              setSearchTrigger((n) => n + 1);
            }}
          >
            {t('common.search')}
          </button>
        </div>
      </div>

      <DataTable
        title={t('legalKnowledge.archive.tableTitle')}
        columns={columns}
        rows={rows}
        rowKey={(r) => r.extracted_case_outcome_id}
        loading={loading}
        defaultRowsPerPage={10}
        rowsPerPageOptions={[5, 10, 15, 20]}
        previousLabel={t('common.previous')}
        nextLabel={t('common.next')}
        serverPaging={{
          totalCount,
          totalPages,
          page,
          pageSize,
          onPageChange: (p) => {
            setLoading(true);
            setPage(p);
          },
          onPageSizeChange: (s) => {
            setLoading(true);
            setPageSize(s);
            setPage(0);
          },
        }}
        serverSort={{
          key: sortKey,
          dir: sortDir,
          onSortChange: (key, dir) => {
            setLoading(true);
            setSortKey(key);
            setSortDir(dir);
            setPage(0);
          },
        }}
      />
    </>
  );
}
